import { ROLE_LABELS, ROLE_COLORS, timeAgo, formatDate, actionLabel } from './shared.js';

export default function UserDetailPanel({
  selectedDetail, currentUser, isAdmin, theme,
  activeTab, setActiveTab,
  activityLog, activityLoading,
  onRoleChange, onDeactivate, onReactivate,
  onEdit, onResetPassword,
}) {
  if (!selectedDetail) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: theme.textFaint, fontSize: 12 }}>
        User not found
      </div>
    );
  }

  return (
    <>
      {/* User Header */}
      <div style={{
        padding: '20px 24px', borderBottom: `1px solid ${theme.borderLight}`,
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: selectedDetail.color || '#4F8EF7',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 700, color: '#fff', flexShrink: 0,
        }}>{selectedDetail.initials || '??'}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
            {selectedDetail.name}
            {currentUser?.id === selectedDetail.id && (
              <span style={{ fontSize: 10, fontWeight: 500, color: theme.textMuted, marginLeft: 8 }}>(you)</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>{selectedDetail.email}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <span style={{
              fontSize: 10, fontWeight: 600,
              color: ROLE_COLORS[selectedDetail.role] || theme.accent,
              background: `${ROLE_COLORS[selectedDetail.role] || theme.accent}18`,
              padding: '2px 8px', borderRadius: 4,
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>{ROLE_LABELS[selectedDetail.role] || selectedDetail.role}</span>
            {!selectedDetail.is_active && (
              <span style={{
                fontSize: 10, fontWeight: 600, color: theme.red,
                background: theme.redBg, padding: '2px 8px', borderRadius: 4,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>Inactive</span>
            )}
          </div>
        </div>
        {isAdmin && selectedDetail.id !== currentUser?.id && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => onEdit(selectedDetail)}
              style={{
                padding: '6px 12px', fontSize: 11, fontWeight: 500,
                background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                borderRadius: 6, color: theme.textSecondary,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Edit</button>
            <button
              onClick={() => onResetPassword(selectedDetail.id)}
              style={{
                padding: '6px 12px', fontSize: 11, fontWeight: 500,
                background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                borderRadius: 6, color: theme.textSecondary,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Reset Password</button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${theme.borderLight}`, padding: '0 24px' }}>
        {['overview', 'activity'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 16px', fontSize: 12, fontWeight: 600,
              background: 'none', border: 'none',
              borderBottom: activeTab === tab ? `2px solid ${theme.accent}` : '2px solid transparent',
              color: activeTab === tab ? theme.accent : theme.textMuted,
              cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize',
            }}
          >{tab}</button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {activeTab === 'overview' ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
              <StatCard label="Fonts Uploaded" value={selectedDetail.fonts_uploaded || 0} theme={theme} />
              <StatCard label="Active Fonts" value={selectedDetail.active_fonts || 0} theme={theme} />
              <StatCard label="Last Login" value={timeAgo(selectedDetail.last_login_at)} theme={theme} isText />
            </div>

            <div style={{ marginBottom: 24 }}>
              <h4 style={{ fontSize: 11, fontWeight: 600, color: theme.textFaint, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 16px' }}>
                <InfoRow label="Email" value={selectedDetail.email} theme={theme} />
                <InfoRow label="Role" value={ROLE_LABELS[selectedDetail.role]} theme={theme} />
                <InfoRow label="Joined" value={formatDate(selectedDetail.created_at)} theme={theme} />
                <InfoRow label="Last Login" value={formatDate(selectedDetail.last_login_at)} theme={theme} />
                <InfoRow label="Status" value={selectedDetail.is_active ? 'Active' : 'Inactive'} theme={theme} />
              </div>
            </div>

            {isAdmin && selectedDetail.id !== currentUser?.id && (
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontSize: 11, fontWeight: 600, color: theme.textFaint, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Role Management</h4>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['admin', 'editor', 'viewer'].map(role => (
                    <button
                      key={role}
                      onClick={() => onRoleChange(selectedDetail.id, role)}
                      style={{
                        padding: '8px 16px', fontSize: 11, fontWeight: 600,
                        background: selectedDetail.role === role ? `${ROLE_COLORS[role]}18` : theme.bgInput,
                        border: `1px solid ${selectedDetail.role === role ? `${ROLE_COLORS[role]}40` : theme.borderMedium}`,
                        borderRadius: 7,
                        color: selectedDetail.role === role ? ROLE_COLORS[role] : theme.textMuted,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >{ROLE_LABELS[role]}</button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 8 }}>
                  {selectedDetail.role === 'admin' && 'Full access: manage users, upload fonts, edit everything'}
                  {selectedDetail.role === 'editor' && 'Can upload fonts, manage collections, and edit metadata'}
                  {selectedDetail.role === 'viewer' && 'Can browse and activate fonts, view collections'}
                </div>
              </div>
            )}

            {isAdmin && selectedDetail.id !== currentUser?.id && (
              <div style={{ padding: 16, borderRadius: 10, border: `1px solid ${theme.redBorder}`, background: theme.redBg }}>
                <h4 style={{ fontSize: 11, fontWeight: 600, color: theme.red, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Danger Zone</h4>
                {selectedDetail.is_active ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>Deactivate User</div>
                      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>User will be unable to log in. Can be reactivated later.</div>
                    </div>
                    <button
                      onClick={() => onDeactivate(selectedDetail.id, selectedDetail.name)}
                      style={{ padding: '6px 16px', fontSize: 11, fontWeight: 600, background: theme.red, border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
                    >Deactivate</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>Reactivate User</div>
                      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>Restore access for this user.</div>
                    </div>
                    <button
                      onClick={() => onReactivate(selectedDetail.id, selectedDetail.name)}
                      style={{ padding: '6px 16px', fontSize: 11, fontWeight: 600, background: theme.green, border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
                    >Reactivate</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            {activityLoading ? (
              <div style={{ textAlign: 'center', padding: 20, color: theme.textMuted, fontSize: 12 }}>Loading activity...</div>
            ) : activityLog.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint, fontSize: 12 }}>No activity recorded</div>
            ) : (
              <div>
                {activityLog.map((a, i) => (
                  <div key={a.id || i} style={{
                    padding: '10px 0',
                    borderBottom: i < activityLog.length - 1 ? `1px solid ${theme.borderLight}` : 'none',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: theme.accent, marginTop: 6, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: theme.text, fontWeight: 500 }}>{actionLabel(a.action)}</div>
                      {a.details && (
                        <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 2 }}>
                          {typeof a.details === 'object'
                            ? Object.entries(a.details).map(([k, v]) => `${k}: ${v}`).join(' · ')
                            : String(a.details)
                          }
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: theme.textGhost, marginTop: 4 }}>{timeAgo(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function StatCard({ label, value, theme, isText }) {
  return (
    <div style={{ padding: 16, borderRadius: 10, background: theme.bgInput, border: `1px solid ${theme.borderLight}` }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: theme.textFaint, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: isText ? 14 : 22, fontWeight: 700, color: theme.text }}>{value}</div>
    </div>
  );
}

function InfoRow({ label, value, theme }) {
  return (
    <>
      <div style={{ fontSize: 12, color: theme.textFaint, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 12, color: theme.text }}>{value || '—'}</div>
    </>
  );
}
